import React, { useState, useCallback } from "react";
import { Twitter, Linkedin, Facebook, MessageCircle, Mail, Link2, Check, Share2 } from "lucide-react";

/**
 * Boutons de partage rapide d'un article sur les réseaux.
 * Ouvre les intents web en popup ; copie le lien ; utilise l'API
 * Web Share native si disponible (mobile surtout).
 *
 * Props :
 *  - title : titre de l'article (utilisé dans le texte partagé)
 *  - url   : URL à partager (def. URL courante)
 */
const ShareButtons = ({ title = "", url }) => {
    const [copied, setCopied] = useState(false);
    const shareUrl = url || (typeof window !== "undefined" ? window.location.href : "");
    const eUrl = encodeURIComponent(shareUrl);
    const eTitle = encodeURIComponent(title);

    const popup = useCallback((href) => {
        window.open(href, "_blank", "noopener,noreferrer,width=620,height=560");
    }, []);

    const copy = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard indisponible : sélection manuelle en dernier recours.
            window.prompt("Copier le lien :", shareUrl);
        }
    }, [shareUrl]);

    const nativeShare = useCallback(() => {
        if (navigator.share) {
            navigator.share({ title, url: shareUrl }).catch(() => {});
        }
    }, [title, shareUrl]);

    const networks = [
        {
            key: "x",
            label: "X (Twitter)",
            icon: <Twitter size={18} />,
            onClick: () => popup(`https://twitter.com/intent/tweet?url=${eUrl}&text=${eTitle}`)
        },
        {
            key: "linkedin",
            label: "LinkedIn",
            icon: <Linkedin size={18} />,
            onClick: () => popup(`https://www.linkedin.com/sharing/share-offsite/?url=${eUrl}`)
        },
        {
            key: "facebook",
            label: "Facebook",
            icon: <Facebook size={18} />,
            onClick: () => popup(`https://www.facebook.com/sharer/sharer.php?u=${eUrl}`)
        },
        {
            key: "whatsapp",
            label: "WhatsApp",
            icon: <MessageCircle size={18} />,
            onClick: () => popup(`https://wa.me/?text=${eTitle}%20${eUrl}`)
        },
        {
            key: "email",
            label: "E-mail",
            icon: <Mail size={18} />,
            onClick: () => { window.location.href = `mailto:?subject=${eTitle}&body=${eUrl}`; }
        }
    ];

    return (
        <div className="article-share">
            <span className="article-share-label">Partager</span>
            <div className="article-share-list">
                {networks.map((n) => (
                    <button
                        key={n.key}
                        type="button"
                        className={`article-share-btn is-${n.key}`}
                        onClick={n.onClick}
                        aria-label={`Partager sur ${n.label}`}
                        title={n.label}
                    >
                        {n.icon}
                    </button>
                ))}

                <button
                    type="button"
                    className={`article-share-btn is-copy ${copied ? "is-copied" : ""}`}
                    onClick={copy}
                    aria-label="Copier le lien"
                    title={copied ? "Lien copié !" : "Copier le lien"}
                >
                    {copied ? <Check size={18} /> : <Link2 size={18} />}
                </button>

                {typeof navigator !== "undefined" && navigator.share && (
                    <button
                        type="button"
                        className="article-share-btn is-native"
                        onClick={nativeShare}
                        aria-label="Plus d'options de partage"
                        title="Partager…"
                    >
                        <Share2 size={18} />
                    </button>
                )}
            </div>
            {copied && <span className="article-share-copied" role="status">Lien copié&nbsp;!</span>}
        </div>
    );
};

export default ShareButtons;
